import { useState, useEffect, useCallback, useRef } from "react";
import { api, handleAuthFailure } from "../data/api";
import { generateDeviceId } from "../data/ids";
import { maxChannelsForDevice, maxChannelsFor, normalizeOutputType, outputTypeLabel, OUTPUT_TYPE_OPTIONS } from "../data/deviceModels";
import Badge from "../components/Badge";
import Pill from "../components/Pill";
import StatCard from "../components/StatCard";
import HealthBar from "../components/HealthBar";
import Toggle from "../components/Toggle";
import Btn from "../components/Btn";
import { TH, TD } from "../components/TableWrap";
import { useTheme } from "../context/ThemeContext";

function DeviceConfig({ devId, onBack }) {
  const [device, setDevice]   = useState(null);
  const [channels, setChannels] = useState([]);
  const [assignedChannels, setAssignedChannels] = useState([]); // for multi-channel devices
  const [peerUsed, setPeerUsed] = useState([]); // channels used by peer 3568 devices on same affiliate
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [srtUrl, setSrtUrl]   = useState("");
  const [audio, setAudio]     = useState("default");
  const [enabled, setEnabled] = useState(true);
  const [addingChannel, setAddingChannel] = useState(false);
  const [newChannelId, setNewChannelId]   = useState("");
  // Where the box sends each channel's UDP output. Server-assigned: the box uses
  // these verbatim instead of deriving a port from its own env.
  const [udpEdits, setUdpEdits] = useState({});   // channel_id -> {ip, port}
  const [newUdpIp, setNewUdpIp]     = useState("");
  const [newUdpPort, setNewUdpPort] = useState("");
  const [udpSavingId, setUdpSavingId] = useState(null);
  const [removingId, setRemovingId]   = useState(null);

  const loadDevice = async () => {
    try {
      const [dev, chs] = await Promise.all([
        api.devices.get(devId),
        api.channels.list().catch(() => []),
      ]);
      setDevice(dev);
      setChannels(chs);
      setSrtUrl(dev?.channel_url || "");
      setAudio(dev?.audio_device || "default");
      setEnabled(dev?.enabled !== false);

      // GET /devices/:id returns the device's own enabled channels, so a
      // multi-channel box shows its assignments even when it has no affiliate
      // (channel-limits below is only needed to find peer-held channels).
      setAssignedChannels((dev?.channels || []).map(c => c.channel_id).filter(Boolean));

      const maxCh = maxChannelsForDevice(dev);
      if (maxCh > 1 && dev?.affiliate_id) {
        const limits = await api.devices.channelLimits(dev.affiliate_id).catch(() => []);
        const myType = dev.device_type || 'box';
        const used = limits
          .filter(l => l.device_id !== devId)
          .filter(l => (l.device_type || 'box') === myType)
          .flatMap(l => l.assigned_channels || [])
          .filter(Boolean);
        setPeerUsed(used);

        const myChannels = limits.find(l => l.device_id === devId);
        setAssignedChannels(myChannels?.assigned_channels?.filter(Boolean) || []);
      }
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError(e.message || "Failed to load device");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDevice(); }, [devId]);

  const handleSave = async () => {
    if (!device?.channel_id) return;
    setSaving(true); setError(null);
    try {
      await api.devices.saveConfig(devId, {
        channel_id: device.channel_id,
        channel_url: srtUrl,
        audio_device: audio,
        enabled,
      });
      onBack();
    } catch (e) {
      setError(e.message || "Failed to save configuration");
      setSaving(false);
    }
  };

  const handleAddChannel = async () => {
    if (!newChannelId) return;
    setSaving(true); setError(null);
    try {
      // Blank UDP fields are sent as null, not '' — the server reads null as
      // "unset" (box falls back to its own port) and would reject a half pair.
      await api.devices.assignChannel(devId, {
        channel_id: newChannelId,
        channel_url: channels.find(c => c.id === newChannelId)?.master_srt_url || '',
        display_port: 1,
        audio_device: 'default',
        udp_out_ip: newUdpIp.trim() || null,
        udp_out_port: newUdpPort.trim() || null,
      });
      setNewChannelId("");
      setNewUdpIp(""); setNewUdpPort("");
      setAddingChannel(false);
      await loadDevice();
    } catch (e) {
      setError(e.message || "Failed to assign channel");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveChannel = async (chId) => {
    setRemovingId(chId); setError(null);
    // Drop the row immediately; loadDevice() below reconciles with the server,
    // which also refreshes peerUsed/slots so the channel becomes addable again.
    setAssignedChannels(prev => prev.filter(id => id !== chId));
    setUdpEdits(prev => { const n = { ...prev }; delete n[chId]; return n; });
    try {
      await api.devices.unassignChannel(devId, chId);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      // 404 = the binding was already removed elsewhere (e.g. Edit Affiliate).
      // That's the state we want anyway — reconcile without an error.
      if (e.status !== 404) setError(e.message || "Failed to remove channel");
    } finally {
      await loadDevice();
      setRemovingId(null);
    }
  };

  if (loading) return <div style={{ fontSize:12, color:"var(--text-dim)", padding:"40px 0", textAlign:"center" }}>Loading device…</div>;
  if (!device) return null;

  const maxChannels = maxChannelsForDevice(device);
  const isMulti = maxChannels > 1;
  const channelName = channels.find(c => c.id === device.channel_id)?.display_name || "";
  const availableToAdd = channels.filter(c =>
    !assignedChannels.includes(c.id) && !peerUsed.includes(c.id)
  );
  const slotsLeft = maxChannels - assignedChannels.length;

  // The binding rows (which carry udp_out_ip/udp_out_port), keyed by channel.
  // assignedChannels holds only ids, so the destination has to come from here.
  const bindings = Object.fromEntries((device.channels || []).map(c => [c.channel_id, c]));

  const udpValue = (chId, field) => {
    const edit = udpEdits[chId];
    if (edit && edit[field] !== undefined) return edit[field];
    const b = bindings[chId] || {};
    return (field === "ip" ? b.udp_out_ip : b.udp_out_port) ?? "";
  };
  const setUdpValue = (chId, field, val) =>
    setUdpEdits(prev => ({ ...prev, [chId]: { ...(prev[chId] || {}), [field]: val } }));

  // What a binding INHERITS when it sets no override: the channel's canonical
  // multicast destination (channels.mcast_*). This is exactly what /config
  // resolves — COALESCE(dc.udp_out_*, c.mcast_*) — so what is shown here is
  // what the box will actually be told.
  //
  // This replaces a device-scoped suggestion that offered 239.10.10.10 and the
  // next free port from 5001. That proposed the legacy one-group-many-ports
  // scheme: IGMP snooping filters on the GROUP, not the port, so every receiver
  // joining to watch one channel pulled all six onto its NIC and discarded the
  // rest. Accepting that default wrote an override reproducing the very
  // addressing the per-channel groups exist to replace.
  //
  // Read from the channel list rather than the binding so it also works for a
  // channel that is not assigned yet (the add-channel row below).
  const inheritedDest = (chId) => {
    if (!chId) return null;
    const c = channels.find(x => x.id === chId) || bindings[chId] || {};
    return (c.mcast_ip && c.mcast_port)
      ? { ip: String(c.mcast_ip), port: String(c.mcast_port) }
      : null;
  };

  const saveUdpDest = async (chId) => {
    const ip = String(udpValue(chId, "ip") || "").trim();
    const port = String(udpValue(chId, "port") || "").trim();
    setUdpSavingId(chId); setError(null);
    try {
      // Both empty clears the destination, which reverts that channel to the
      // box's env-derived fallback port.
      await api.devices.saveConfig(devId, {
        channel_id: chId,
        udp_out_ip: ip || null,
        udp_out_port: port || null,
      });
      setUdpEdits(prev => { const n = { ...prev }; delete n[chId]; return n; });
      await loadDevice();
    } catch (e) {
      setError(e.message || "Failed to save UDP destination");
    } finally {
      setUdpSavingId(null);
    }
  };

  return (
    <>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18, paddingBottom:14, borderBottom:`1px solid var(--border)` }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"var(--ink)", fontFamily:"'IBM Plex Mono', monospace" }}>{device.id}</div>
          <div style={{ fontSize:11, color:"var(--text-dim)", marginTop:2 }}>
            {device.device_type === "self-srt-connect" ? "" : device.device_model}
            {device.device_type !== "self-srt-connect" && (
              <span style={{ marginLeft:8, background:"var(--surface-alt)", border:"1px solid var(--border)", borderRadius:4, padding:"1px 7px", fontSize:10, fontWeight:700 }}>
                {outputTypeLabel(device.output_type)}
              </span>
            )}
            <span style={{ marginLeft:8, background:"var(--blue-bg,#eff6ff)", color:"var(--blue,#2563eb)", border:"1px solid var(--blue-border,#bfdbfe)", borderRadius:4, padding:"1px 7px", fontSize:10, fontWeight:700 }}>
              {assignedChannels.length}/{maxChannels} channel{maxChannels === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <Btn onClick={onBack}>← Back</Btn>
      </div>

      {device.device_type !== "self-srt-connect" && (
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, padding:"10px 12px", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:8 }}>
          <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600 }}>Output Type</div>
          <select
            value={normalizeOutputType(device.output_type)}
            disabled={saving}
            onChange={async (e) => {
              const next = e.target.value;
              setSaving(true); setError(null);
              try {
                // The server refuses the switch (409) if more channels are
                // assigned than the new type allows, rather than silently
                // dropping them — surface that message as-is.
                await api.devices.update(devId, { output_type: next });
                await loadDevice();
              } catch (err) {
                setError(err.message || "Failed to change output type");
              } finally {
                setSaving(false);
              }
            }}
            style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"6px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
          >
            {OUTPUT_TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ fontSize:10, color:"var(--text-dim)" }}>
            {OUTPUT_TYPE_OPTIONS.find(o => o.value === normalizeOutputType(device.output_type))?.hint}
          </div>
        </div>
      )}

      {error && (
        <div style={{ background:"var(--red-bg)", border:`1px solid var(--red-border)`, borderRadius:7, padding:"8px 12px", marginBottom:12, fontSize:12, color:"var(--red)" }}>
          {error}
        </div>
      )}

      {isMulti ? (
        /* ── multi-channel models (RK3568 family, up to 6): channel list view ── */
        <div>
          <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, overflow:"hidden", marginBottom:14 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--surface-alt)" }}>
                  <TH>Assigned Channels</TH>
                  <TH>Display Name</TH>
                  <TH>UDP Out (IP : Port)</TH>
                  <TH>Status</TH>
                  <TH></TH>
                </tr>
              </thead>
              <tbody>
                {assignedChannels.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding:"16px", fontSize:12, color:"var(--text-dim)", textAlign:"center" }}>No channels assigned yet.</td></tr>
                ) : assignedChannels.map(chId => {
                  const ch = channels.find(c => c.id === chId);
                  const dirty = !!udpEdits[chId];
                  // No per-binding override set. That no longer means "box
                  // default": if the channel has a canonical destination the
                  // binding inherits it, and only a channel without one falls
                  // through to the box's UDP_PORT_BASE + slot.
                  const unset = !bindings[chId]?.udp_out_ip || !bindings[chId]?.udp_out_port;
                  const inh = inheritedDest(chId);
                  return (
                    <tr key={chId}>
                      <TD mono>{chId}</TD>
                      <TD>{ch?.display_name || "—"}</TD>
                      <TD>
                        <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                          <input
                            value={udpValue(chId, "ip")}
                            onChange={e => setUdpValue(chId, "ip", e.target.value)}
                            placeholder={inh ? inh.ip : "box default"}
                            style={{ width:112, background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:5, padding:"4px 6px", fontSize:11, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }}
                          />
                          <span style={{ fontSize:11, color:"var(--text-dim)" }}>:</span>
                          <input
                            value={udpValue(chId, "port")}
                            onChange={e => setUdpValue(chId, "port", e.target.value)}
                            placeholder={inh ? inh.port : "—"}
                            style={{ width:56, background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:5, padding:"4px 6px", fontSize:11, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }}
                          />
                          {dirty && (
                            <Btn primary onClick={() => saveUdpDest(chId)} disabled={udpSavingId === chId}>
                              {udpSavingId === chId ? "…" : "Save"}
                            </Btn>
                          )}
                          {!dirty && unset && (
                            inh ? (
                              <span title={`Inherits the channel's destination: udp://${inh.ip}:${inh.port}. Set a value here only to override it for this box.`}
                                    style={{ fontSize:10, color:"var(--text-dim)" }}>inherited</span>
                            ) : (
                              <span title="This channel has no canonical destination, so the box falls back to its own UDP_PORT_BASE + slot port."
                                    style={{ fontSize:10, color:"var(--text-dim)" }}>box default</span>
                            )
                          )}
                        </div>
                      </TD>
                      <TD><span style={{ fontSize:11, color:"var(--green,#16a34a)", fontWeight:600 }}>✓ Active</span></TD>
                      <TD>
                        <button
                          onClick={() => handleRemoveChannel(chId)}
                          disabled={removingId === chId}
                          title={`Remove ${chId} from this device`}
                          style={{ background:"none", border:"none", color:"var(--text-dim)", cursor: removingId === chId ? "not-allowed" : "pointer", fontSize:14, lineHeight:1, padding:"2px 6px" }}
                        >{removingId === chId ? "…" : "×"}</button>
                      </TD>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {slotsLeft > 0 && (
            addingChannel ? (
              <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:14 }}>
                <select
                  value={newChannelId}
                  onChange={e => setNewChannelId(e.target.value)}
                  style={{ flex:1, background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none" }}
                >
                  <option value="">— Select channel —</option>
                  {availableToAdd.map(c => (
                    <option key={c.id} value={c.id}>{c.id} · {c.display_name}</option>
                  ))}
                </select>
                <input
                  value={newUdpIp}
                  onChange={e => setNewUdpIp(e.target.value)}
                  placeholder={inheritedDest(newChannelId)?.ip || "inherited"}
                  title="UDP output IP for this channel on this box. Leave blank to inherit the channel's canonical destination — override only for a site that needs a different one."
                  style={{ width:118, background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 8px", fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }}
                />
                <input
                  value={newUdpPort}
                  onChange={e => setNewUdpPort(e.target.value)}
                  placeholder={inheritedDest(newChannelId)?.port || "inherited"}
                  title="UDP output port. Leave blank to inherit the channel's canonical port. If set, it must be unique among this device's channels."
                  style={{ width:64, background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"7px 8px", fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }}
                />
                <Btn primary onClick={handleAddChannel} disabled={saving || !newChannelId}>
                  {saving ? "Adding…" : "Add"}
                </Btn>
                <Btn onClick={() => { setAddingChannel(false); setNewChannelId(""); setNewUdpIp(""); setNewUdpPort(""); }}>Cancel</Btn>
              </div>
            ) : (
              <div style={{ marginBottom:14 }}>
                <Btn onClick={() => setAddingChannel(true)}>
                  + Add Channel ({slotsLeft} slot{slotsLeft !== 1 ? "s" : ""} remaining)
                </Btn>
                {peerUsed.length > 0 && (
                  <div style={{ fontSize:11, color:"var(--text-dim)", marginTop:6 }}>
                    {peerUsed.length} channel{peerUsed.length !== 1 ? "s" : ""} unavailable — already assigned to another {device.device_model || "device"} on this affiliate.
                  </div>
                )}
              </div>
            )
          )}
          {slotsLeft === 0 && (
            <div style={{ fontSize:12, color:"var(--text-sub)", marginBottom:14 }}>
              All {maxChannels} channel slots are filled for this device.
            </div>
          )}
        </div>
      ) : (
        /* ── Single-channel devices: existing UI unchanged ── */
        <>
          <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:9, overflow:"hidden", marginBottom:14 }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"var(--surface-alt)" }}>
                  <TH>Channel</TH>
                  <TH>Master SRT URL</TH>
                  <TH>Audio Device</TH>
                  <TH>Enable</TH>
                  <TH></TH>
                </tr>
              </thead>
              <tbody>
                {device.channel_id ? (
                  <tr>
                    <TD>
                      <Pill label={device.channel_id} />
                      {channelName && <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:2 }}>{channelName}</div>}
                    </TD>
                    <TD style={{ width:"100%" }}>
                      <input value={srtUrl} onChange={e => setSrtUrl(e.target.value)}
                        style={{ width:"100%", background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:5, padding:"6px 9px", fontSize:12, color:"var(--ink)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }} />
                    </TD>
                    <TD>
                      <input value={audio} onChange={e => setAudio(e.target.value)}
                        style={{ width:90, background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:5, padding:"6px 8px", fontSize:12, color:"var(--ink)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }} />
                    </TD>
                    <TD><Toggle on={enabled} onChange={setEnabled} /></TD>
                    <TD><Btn small onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn></TD>
                  </tr>
                ) : (
                  <tr><td colSpan={5} style={{ padding:"16px 14px", fontSize:12, color:"var(--text-dim)", textAlign:"center" }}>No channel assigned to this device yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end" }}>
            <Btn primary onClick={handleSave} disabled={saving || !device.channel_id}>
              {saving ? "Saving…" : "Save Configuration"}
            </Btn>
          </div>
        </>
      )}
    </>
  );
}

export default function DeviceList({ initialSearch, readOnly = false }) {
  const { mode } = useTheme();
  const [devices, setDevices] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [allChannelsForAdd, setAllChannelsForAdd] = useState([]);
  const [allModelsForAdd, setAllModelsForAdd]      = useState([]);
  const [takenChannelsForAdd, setTakenChannelsForAdd] = useState([]);
  const [peerBlockedChannels, setPeerBlockedChannels] = useState([]); // channels used by same-type devices on selected affiliate
  const [showAddDevice, setShowAddDevice]   = useState(false);
  const [addDeviceForm, setAddDeviceForm]   = useState({
    affiliateId: '', mac: '', model: '', ip_address: '', channelId: '', channelIds: [], device_type: 'box', output_type: 'ip',
  });
  const [addDeviceError, setAddDeviceError]     = useState(null);
  const [addDeviceSaving, setAddDeviceSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [filterAff, setFilterAff] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState(initialSearch || "");

  useEffect(() => {
    if (initialSearch) setSearch(initialSearch);
  }, [initialSearch]);
  const [configDev, setConfigDev] = useState(null);
  const [inlineEdits, setInlineEdits] = useState({});
  const [inlineSaving, setInlineSaving] = useState({});
  const [currentPage, setCurrentPage]   = useState(1);
  const PAGE_SIZE = 25;
  // Reset to page 1 when filters change
  const resetPage = () => setCurrentPage(1);
  const [showWorksheetModal, setShowWorksheetModal] = useState(false);
  const [worksheetUploading, setWorksheetUploading] = useState(false);
  const [worksheetResult, setWorksheetResult]       = useState(null);
  const worksheetInputRef = useRef(null);

  // ── Worksheet: download blank template ─────────────────────────────────
  const downloadTemplate = () => {
    const headers = ["device_id","mac_address","ip_address","device_model","app_version","affiliate_name","affiliate_id"];
    const example = ["dev-101","AA:BB:CC:DD:EE:FF","192.168.1.101","eBox-3288","3.2.1","Example Affiliate","aff-01"];
    const csv = [headers.join(","), example.join(",")].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a   = Object.assign(document.createElement("a"), { href: url, download: "device_worksheet_template.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Worksheet: parse CSV and submit rows ─────────────────────────────
  const handleWorksheetUpload = async (file) => {
    if (!file) return;
    setWorksheetUploading(true);
    setWorksheetResult(null);
    try {
      const text  = await file.text();
      const lines = text.trim().split("\n").filter(Boolean);
      if (lines.length < 2) throw new Error("File is empty or has no data rows.");

      const headers = lines[0].split(",").map(h => h.trim().toLowerCase().replace(/^"|"$/g, ""));
      const required = ["device_id","mac_address","affiliate_id"];
      const missing  = required.filter(c => !headers.includes(c));
      if (missing.length) throw new Error(`Missing required columns: ${missing.join(", ")}`);

      const col = (name) => headers.indexOf(name);
      const rows = lines.slice(1).map(line => {
        const cols = line.match(/(".*?"|[^,]+)(?=,|$)/g) || line.split(",");
        const v    = cols.map(c => c.trim().replace(/^"|"$/g, ""));
        return {
          id:           v[col("device_id")]    || "",
          mac_address:  v[col("mac_address")]  || "",
          ip_address:   col("ip_address")   >= 0 ? v[col("ip_address")]   || null : null,
          device_model: col("device_model") >= 0 ? v[col("device_model")] || null : null,
          app_version:  col("app_version")  >= 0 ? v[col("app_version")]  || "—"  : "—",
          affiliate_id: v[col("affiliate_id")] || "",
        };
      }).filter(r => r.id && r.mac_address && r.affiliate_id);

      if (!rows.length) throw new Error("No valid rows found — check required columns: device_id, mac_address, affiliate_id.");

      const results = { added: [], errors: [] };
      for (const row of rows) {
        try {
          await api.devices.create(row);
          results.added.push(row.id);
        } catch (e) {
          results.errors.push({ id: row.id, message: e.message || "Failed" });
        }
      }
      setWorksheetResult(results);
      if (results.added.length > 0) await load();
    } catch (e) {
      setWorksheetResult({ added: [], errors: [{ id: "—", message: e.message }] });
    } finally {
      setWorksheetUploading(false);
      if (worksheetInputRef.current) worksheetInputRef.current.value = "";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [devs, affs, chs, models, taken] = await Promise.all([
        api.devices.list({}),
        api.affiliates.list().catch(() => []),
        api.channels.list().catch(() => []),
        api.devices.models(),
        api.devices.takenChannels(),
      ]);

      setDevices(Array.isArray(devs) ? devs : []);
      setAffiliates(
        Array.isArray(affs) ? affs.filter((a) => a.status !== "archived") : [],
      );
      setAllChannelsForAdd(Array.isArray(chs) ? chs : []);
      setAllModelsForAdd(Array.isArray(models) ? models : []);
      setTakenChannelsForAdd(Array.isArray(taken) ? taken : []);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError("Failed to load devices. Is the server running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Add Device handler ────────────────────────────────────────────────

  // Channels already assigned to devices of the SAME type on this affiliate.
  // A channel may appear once per device type (box / self-srt), so only
  // same-type devices block it.
  const refreshPeerBlocked = async (affId, deviceType) => {
    if (!affId) { setPeerBlockedChannels([]); return; }
    try {
      const limits = await api.devices.channelLimits(affId);
      const blocked = limits
        .filter(l => (l.device_type || 'box') === (deviceType || 'box'))
        .flatMap(l => l.assigned_channels || [])
        .filter(Boolean);
      setPeerBlockedChannels(blocked);
    } catch { setPeerBlockedChannels([]); }
  };

  const handleAddDevice = async () => {
    setAddDeviceError(null);
    const { affiliateId, mac, model, ip_address, channelId, channelIds, device_type } = addDeviceForm;
    const output_type = normalizeOutputType(addDeviceForm.output_type);
    const maxChannels = maxChannelsFor(model, output_type);
    const isMulti = maxChannels > 1;
    const selectedChannels = isMulti ? channelIds : (channelId ? [channelId] : []);

    if (selectedChannels.length > maxChannels) {
      setAddDeviceError(`${model || 'This model'} on ${outputTypeLabel(output_type)} supports at most ${maxChannels} channel${maxChannels === 1 ? '' : 's'}.`);
      return;
    }

    // Affiliate is optional — devices can be created Unassigned (no affiliate, no channel).

    let macClean = null;
    if (device_type === 'self-srt-connect') {
      if (!ip_address.trim()) { setAddDeviceError('IP address is required for Self-SRT-Connect devices'); return; }
      const IP_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
      if (!IP_RE.test(ip_address.trim())) { setAddDeviceError('Enter a valid IPv4 address'); return; }
    } else {
      macClean = mac.trim().toUpperCase();
      const MAC_RE = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;
      if (!MAC_RE.test(macClean)) { setAddDeviceError('MAC must be in format AA:BB:CC:DD:EE:FF'); return; }
      if (devices.some(d => d.mac_address?.toUpperCase() === macClean)) {
        setAddDeviceError('A device with this MAC address already exists');
        return;
      }
    }

    // Channel assignment is mandatory for Self-SRT-Connect devices only.
    if (device_type === 'self-srt-connect' && selectedChannels.length === 0) {
      setAddDeviceError('Assign a channel — required for Self-SRT-Connect devices');
      return;
    }

    setAddDeviceSaving(true);
    try {
      const newId = generateDeviceId();
      await api.devices.create({
        id: newId,
        device_type,
        mac_address: macClean,
        ip_address: ip_address.trim() || null,
        device_model: model || null,
        output_type,
        app_version: '—',
        affiliate_id: affiliateId || null,
      });

      // Assign channels sequentially so the backend can validate each one
      for (const chId of selectedChannels) {
        const ch = allChannelsForAdd.find(c => c.id === chId);
        await api.devices.assignChannel(newId, {
          channel_id: chId,
          channel_url: ch?.master_srt_url || '',
          display_port: 1,
          audio_device: 'default',
        });
      }

      setShowAddDevice(false);
      setAddDeviceForm({ affiliateId: '', mac: '', model: '', ip_address: '', channelId: '', channelIds: [], device_type: 'box', output_type: 'ip' });
      await load();
    } catch (e) {
      setAddDeviceError(e.message || 'Failed to add device');
    } finally {
      setAddDeviceSaving(false);
    }
  };

  const toggle = (id) =>
    setExpanded((e) =>
      e.includes(id) ? e.filter((x) => x !== id) : [...e, id],
    );

  const expandAll  = () => setExpanded([...affiliates.map(a => a.id), null]);
  const collapseAll = () => setExpanded([]);
  const allExpanded = expanded.length >= affiliates.length + 1;

  const startInlineEdit = (dev) => {
    setInlineEdits((p) => ({
      ...p,
      [dev.id]: {
        mac_address: dev.mac_address || "",
        ip_address: dev.ip_address || "",
      },
    }));
  };

  const cancelInlineEdit = (devId) => {
    setInlineEdits((p) => {
      const next = { ...p };
      delete next[devId];
      return next;
    });
  };

  const saveInlineEdit = async (devId) => {
    setInlineSaving((p) => ({ ...p, [devId]: true }));

    try {
      await api.devices.patch(devId, inlineEdits[devId]);
      cancelInlineEdit(devId);
      await load();
    } catch (e) {
      alert(e.message || "Failed to save");
    } finally {
      setInlineSaving((p) => ({ ...p, [devId]: false }));
    }
  };

  const handleDeviceStatusChange = async (id, newStatus) => {
    try {
      await api.devices.patch(id, { status: newStatus });
      await load();
    } catch (e) {
      alert(e.message || "Failed to update status");
    }
  };

  const total = devices.length;
  const online      = devices.filter((d) => d.status === "online").length;
  const offline     = devices.filter((d) => d.status === "offline" && d.device_type !== "self-srt-connect").length;
  const degraded    = devices.filter((d) => d.status === "degraded").length;
  const deactivated = devices.filter((d) => d.status === "deactivated").length;

  const affStatus = (affId) => {
    const devs = devices.filter((d) => d.affiliate_id === affId && d.device_type !== "self-srt-connect");
    if (!devs.length) return "active";
    if (devs.some((d) => d.status === "degraded")) return "degraded";
    if (devs.every((d) => d.status === "offline")) return "offline";
    return "active";
  };

  const healthScore = (dev) => {
    if (dev.status === "offline") return null;

    const cpu = Number(dev.cpu_usage);
    const ram = Number(dev.ram_usage);

    if (!cpu && !ram) return null;

    return Math.round(100 - (cpu + ram) / 2);
  };

  if (configDev) {
    return (
      <DeviceConfig
        devId={configDev}
        onBack={() => {
          setConfigDev(null);
          load();
        }}
      />
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 18,
          paddingBottom: 14,
          borderBottom: `1px solid var(--border)`,
        }}
      >
        <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>
          Device List
        </h1>

        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          <button onClick={allExpanded ? collapseAll : expandAll}
            style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:6, padding:"5px 10px", fontSize:11, color:"var(--text-sub)", cursor:"pointer", fontFamily:"inherit" }}>
            {allExpanded ? "⊟ Collapse All" : "⊞ Expand All"}
          </button>
          {!readOnly && (
            <Btn onClick={() => { setShowAddDevice(true); setAddDeviceError(null); }}>+ Add Device</Btn>
          )}
          <Btn primary onClick={() => { setShowWorksheetModal(true); setWorksheetResult(null); }}>+ Upload Worksheet</Btn>
        </div>
      </div>


      {error && (
        <div
          style={{
            background: "var(--red-bg)",
            border: `1px solid var(--red-border)`,
            borderRadius: 7,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-dim)",
            padding: "40px 0",
            textAlign: "center",
          }}
        >
          Loading devices…
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            <StatCard label="Total" value={total} />
            <StatCard
              label="Online"
              value={online}
              note={
                total ? `${Math.round((online / total) * 100)}% of fleet` : ""
              }
            />
            <StatCard label="Offline" value={offline} />
            <StatCard label="Degraded" value={degraded} />
          </div>

          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[
              ["all", "All Devices"],
              ["__unassigned__", "Unassigned"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilterAff(key)}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: `1px solid ${filterAff === key ? "var(--ink)" : "var(--border)"}`,
                  background: filterAff === key ? "var(--ink)" : "var(--surface)",
                  color: filterAff === key ? "#fff" : "var(--text-sub)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: search.trim() ? 6 : 14 }}>
            <select
              value={filterAff}
              onChange={(e) => setFilterAff(e.target.value)}
              style={{
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 12,
                color: "var(--text)",
                fontFamily: "inherit",
                outline: "none",
              }}
            >
              <option value="all">All Affiliates</option>
              {affiliates.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.id} — {a.org_name}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 12,
                color: "var(--text)",
                fontFamily: "inherit",
                outline: "none",
              }}
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="degraded">Degraded</option>
              <option value="deactivated">Deactivated</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 12,
                color: "var(--text)",
                fontFamily: "inherit",
                outline: "none",
              }}
            >
              <option value="all">All Types</option>
              <option value="box">Box</option>
              <option value="self-srt-connect">Self-SRT-Connect</option>
            </select>

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search device ID or MAC…"
              style={{
                background: "var(--surface)",
                border: `1px solid var(--border)`,
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 12,
                color: "var(--text)",
                outline: "none",
                flex: 1,
              }}
            />
          </div>
          {search.trim() && (() => {
            const matchCount = affiliates.reduce((acc, aff) =>
              acc + devices.filter(d =>
                d.affiliate_id === aff.id &&
                (d.id?.toLowerCase().includes(search.toLowerCase()) ||
                 d.mac_address?.toLowerCase().includes(search.toLowerCase()) ||
                 d.ip_address?.toLowerCase().includes(search.toLowerCase()))
              ).length, 0);
            return (
              <div style={{ fontSize:12, marginBottom:10, padding:"5px 10px", borderRadius:6, display:"inline-block",
                background: matchCount > 0 ? "var(--green-bg,#f0fdf4)" : "var(--red-bg,#fef2f2)",
                color: matchCount > 0 ? "var(--green,#059669)" : "var(--red,#dc2626)",
                border: `1px solid ${matchCount > 0 ? "var(--green-border,#6ee7b7)" : "var(--red-border,#fca5a5)"}`,
              }}>
                {matchCount > 0 ? `✓ ${matchCount} device${matchCount!==1?"s":""} match "${search}"` : `No devices match "${search}"`}
              </div>
            );
          })()}

          <div
            style={{
              background: "var(--surface)",
              border: `1px solid var(--border)`,
              borderRadius: 9,
              overflow: "hidden",
            }}
          >
            {(() => {
              const allAffs = filterAff === "all"
                ? [...affiliates, { id: null, org_name: "Unassigned" }]
                : filterAff === "__unassigned__"
                  ? [{ id: null, org_name: "Unassigned" }]
                  : affiliates.filter((a) => a.id === filterAff);
              const totalPages = Math.ceil(allAffs.length / PAGE_SIZE);
              const pageAffs   = allAffs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
              return (
                <>
                  {pageAffs.map((aff, ai, arr) => {
                const devs = devices.filter(
                  (d) =>
                    d.affiliate_id === aff.id &&
                    (filterStatus === "all" || d.status === filterStatus) &&
                    (filterType === "all" || (d.device_type || "box") === filterType) &&
                    (search === "" ||
                      d.id?.toLowerCase().includes(search.toLowerCase()) ||
                      d.mac_address?.toLowerCase().includes(search.toLowerCase()) ||
                      d.ip_address?.toLowerCase().includes(search.toLowerCase())),
                );

                const isExp = expanded.includes(aff.id);

                return (
                  <div
                    key={aff.id ?? "unassigned"}
                    style={{
                      borderBottom:
                        ai < arr.length - 1 ? `1px solid var(--border)` : "none",
                    }}
                  >
                    <div
                      onClick={() => toggle(aff.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 14px",
                        cursor: "pointer",
                        background: "var(--surface-alt)",
                        userSelect: "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            color: "var(--text-dim)",
                            fontSize: 9,
                            transition: "transform 0.18s",
                            transform: isExp ? "rotate(90deg)" : "rotate(0deg)",
                            display: "inline-block",
                          }}
                        >
                          ▶
                        </span>
                        <span
                          style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "var(--ink)",
                          }}
                        >
                          {aff.id}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--text-sub)" }}>
                          {aff.org_name}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                          · {devs.length} device{devs.length !== 1 ? "s" : ""} ·{" "}
                          {devs.filter((d) => d.status === "online").length}{" "}
                          online
                        </span>
                      </div>
                      <Badge status={affStatus(aff.id)} />
                    </div>

                    {isExp && devs.length > 0 && (
                      <table
                        style={{ width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead>
                          <tr>
                            <TH>Device ID</TH>
                            <TH>Type</TH>
                            <TH>MAC Address</TH>
                            <TH>IP Address</TH>
                            <TH>Channel</TH>
                            <TH>Model</TH>
                            <TH>Status</TH>
                            <TH>Health</TH>
                            <TH>Actions</TH>
                          </tr>
                        </thead>
                        <tbody>
                          {devs.map((dev) => {
                            const h = healthScore(dev);
                            const editing = inlineEdits[dev.id];
                            const saving = inlineSaving[dev.id];

                            return (
                              <tr
                                key={dev.id}
                                style={{
                                  background: editing
                                    ? "var(--surface-alt)"
                                    : undefined,
                                }}
                              >
                                <TD mono style={{ fontWeight: 500 }}>
                                  {dev.id}
                                </TD>

                                <TD>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 8px",
                                      borderRadius: 5,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      letterSpacing: "0.04em",
                                      textTransform: "uppercase",
                                      background:
                                        dev.device_type === "self-srt-connect"
                                          ? "var(--blue-bg, #eff6ff)"
                                          : "var(--surface-alt)",
                                      color:
                                        dev.device_type === "self-srt-connect"
                                          ? "var(--blue, #2563eb)"
                                          : "var(--text-sub)",
                                      border: `1px solid ${
                                        dev.device_type === "self-srt-connect"
                                          ? "var(--blue-border, #bfdbfe)"
                                          : "var(--border)"
                                      }`,
                                    }}
                                  >
                                    {dev.device_type === "self-srt-connect" ? "Self-SRT" : "Box"}
                                  </span>
                                </TD>

                                <TD mono sub>
                                  {editing && dev.device_type !== "self-srt-connect" ? (
                                    <input
                                      value={editing.mac_address}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setInlineEdits((p) => ({
                                          ...p,
                                          [dev.id]: {
                                            ...p[dev.id],
                                            mac_address: val,
                                          },
                                        }));
                                      }}
                                      style={{
                                        width: "100%",
                                        minWidth: 130,
                                        background: "var(--surface)",
                                        border: `1px solid var(--border-strong)`,
                                        borderRadius: 5,
                                        padding: "4px 7px",
                                        fontSize: 11,
                                        fontFamily:
                                          "'IBM Plex Mono', monospace",
                                        color: "var(--ink)",
                                        outline: "none",
                                      }}
                                    />
                                  ) : (
                                    dev.mac_address || "—"
                                  )}
                                </TD>

                                <TD mono sub>
                                  {editing ? (
                                    <input
                                      value={editing.ip_address}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setInlineEdits((p) => ({
                                          ...p,
                                          [dev.id]: {
                                            ...p[dev.id],
                                            ip_address: val,
                                          },
                                        }));
                                      }}
                                      placeholder="e.g. 192.168.1.100"
                                      style={{
                                        width: "100%",
                                        minWidth: 120,
                                        background: "var(--surface)",
                                        border: `1px solid var(--border-strong)`,
                                        borderRadius: 5,
                                        padding: "4px 7px",
                                        fontSize: 11,
                                        fontFamily:
                                          "'IBM Plex Mono', monospace",
                                        color: "var(--ink)",
                                        outline: "none",
                                      }}
                                    />
                                  ) : (
                                    dev.ip_address || "—"
                                  )}
                                </TD>

                                <TD>
                                  {/* A multi-channel box holds up to 6 bindings; show
                                      them all rather than just the first. */}
                                  {(dev.channels || []).length > 0 ? (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                      {dev.channels.map((ch) => {
                                        // Effective destination, the same
                                        // COALESCE(dc.udp_out_*, c.mcast_*) that
                                        // /config applies. Reading udp_out_* alone
                                        // would report "no destination" for every
                                        // binding that inherits one — which is now
                                        // almost all of them.
                                        const overridden = !!(ch.udp_out_ip && ch.udp_out_port);
                                        const ip   = ch.udp_out_ip   || ch.mcast_ip;
                                        const port = ch.udp_out_port || ch.mcast_port;
                                        const dest = ip && port;
                                        return (
                                          <Pill
                                            key={ch.channel_id}
                                            label={
                                              dest
                                                ? `${ch.channel_name || ch.channel_id} → :${port}`
                                                : (ch.channel_name || ch.channel_id)
                                            }
                                            title={
                                              dest
                                                ? `${ch.channel_id} → udp://${ip}:${port}`
                                                  + (overridden
                                                       ? " (override set on this box)"
                                                       : " (inherited from the channel)")
                                                : `${ch.channel_id} — no destination on the binding or the channel; the box falls back to its own UDP_PORT_BASE + slot`
                                            }
                                          />
                                        );
                                      })}
                                    </div>
                                  ) : dev.channel_id ? (
                                    <Pill label={dev.channel_name} />
                                  ) : (
                                    <span
                                      style={{ fontSize: 11, color: "var(--text-dim)" }}
                                    >
                                      —
                                    </span>
                                  )}
                                </TD>

                                <TD sub>{dev.device_type === "self-srt-connect" ? "" : (dev.device_model || "—")}</TD>

                                <TD>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                    }}
                                  >
                                    {dev.device_type === "self-srt-connect" ? (
                                      <span style={{ color: "var(--text-dim)" }}>—</span>
                                    ) : editing ? (
                                      <select
                                        value={dev.status || "offline"}
                                        onChange={(e) =>
                                          handleDeviceStatusChange(
                                            dev.id,
                                            e.target.value,
                                          )
                                        }
                                        style={{
                                          background: "var(--surface)",
                                          border: `1px solid var(--border)`,
                                          borderRadius: 6,
                                          padding: "5px 8px",
                                          fontSize: 12,
                                          color: "var(--text)",
                                          fontFamily: "inherit",
                                          outline: "none",
                                        }}
                                      >
                                        <option value="online">Online</option>
                                        <option value="offline">Offline</option>
                                        {/* <option value="degraded">Degraded</option> */}
                                        <option value="deactivated">Deactivated</option>
                                      </select>
                                    ) : (
                                      <Badge status={dev.status} />
                                    )}
                                  </div>
                                </TD>

                                <TD>
                                  {h !== null ? (
                                    <HealthBar value={h} />
                                  ) : (
                                    <span
                                      style={{ fontSize: 11, color: "var(--text-dim)" }}
                                    >
                                      —
                                    </span>
                                  )}
                                </TD>

                                <TD>
                                  <div style={{ display: "flex", gap: 5 }}>
                                    {editing ? (
                                      <>
                                        <Btn
                                          small
                                          onClick={() => saveInlineEdit(dev.id)}
                                          disabled={saving}
                                        >
                                          {saving ? "…" : "Save"}
                                        </Btn>
                                        <Btn
                                          small
                                          onClick={() =>
                                            cancelInlineEdit(dev.id)
                                          }
                                        >
                                          Cancel
                                        </Btn>
                                      </>
                                    ) : (
                                      <>
                                        {!readOnly && (
                                          <>
                                            <Btn
                                              small
                                              onClick={() => startInlineEdit(dev)}
                                            >
                                              Edit
                                            </Btn>
                                            <Btn
                                              small
                                              onClick={() => setConfigDev(dev.id)}
                                            >
                                              Config
                                            </Btn>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </TD>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}

                    {isExp && devs.length === 0 && (
                      <div
                        style={{
                          padding: "14px 22px",
                          fontSize: 12,
                          color: "var(--text-dim)",
                        }}
                      >
                        No devices match the current filter.
                      </div>
                    )}
                  </div>
                );
              })}
                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"14px 0", borderTop:"1px solid var(--border)", marginTop:4 }}>
                      <button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1}
                        style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:6, padding:"5px 12px", fontSize:12, color:currentPage===1?"var(--text-dim)":"var(--ink)", cursor:currentPage===1?"default":"pointer", fontFamily:"inherit" }}>
                        ← Prev
                      </button>
                      <span style={{ fontSize:12, color:"var(--text-sub)" }}>Page {currentPage} of {totalPages}</span>
                      <button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage===totalPages}
                        style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:6, padding:"5px 12px", fontSize:12, color:currentPage===totalPages?"var(--text-dim)":"var(--ink)", cursor:currentPage===totalPages?"default":"pointer", fontFamily:"inherit" }}>
                        Next →
                      </button>
                    </div>
                  )}
                </>
              );
            })()}

            {filterAff !== "__unassigned__" &&
              affiliates.filter((a) => filterAff === "all" || a.id === filterAff)
              .length === 0 && (
              <div
                style={{
                  padding: "28px 14px",
                  textAlign: "center",
                  fontSize: 12,
                  color: "var(--text-dim)",
                }}
              >
                No affiliates found.
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Add Device Modal ── */}
      {showAddDevice && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowAddDevice(false); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.35)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:400 }}
        >
          <div style={{ background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:12, width:440, maxHeight:"85vh", overflowY:"auto", boxShadow:"0 8px 32px rgba(0,0,0,0.15)", padding:24, fontFamily:"'DM Sans', system-ui, sans-serif" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
              <div style={{ fontSize:15, fontWeight:700, color:"var(--ink)" }}>Add Device</div>
              <button onClick={() => setShowAddDevice(false)} style={{ background:"none", border:`1px solid var(--border)`, borderRadius:6, padding:"4px 10px", fontSize:13, color:"var(--text-dim)", cursor:"pointer" }}>×</button>
            </div>

            {addDeviceError && (
              <div style={{ background:"var(--red-bg,#fef2f2)", border:"1px solid var(--red-border,#fca5a5)", borderRadius:7, padding:"9px 12px", fontSize:12, color:"var(--red,#dc2626)", marginBottom:14 }}>
                {addDeviceError}
              </div>
            )}

            <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>Affiliate</div>
                <select
                  value={addDeviceForm.affiliateId}
                  onChange={async (e) => {
                    const affId = e.target.value;
                    setAddDeviceForm(p => ({ ...p, affiliateId: affId, channelId: '', channelIds: [] }));
                    await refreshPeerBlocked(affId, addDeviceForm.device_type);
                  }}
                  style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                >
                  <option value="">— Unassigned (no affiliate, no channel) —</option>
                  {affiliates.map(a => (
                    <option key={a.id} value={a.id}>{a.org_name} ({a.id})</option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>Device Type *</div>
                <select
                  value={addDeviceForm.device_type}
                  onChange={async (e) => {
                    const dt = e.target.value;
                    setAddDeviceForm(p => ({ ...p, device_type: dt, mac: '', channelId: '', channelIds: [] }));
                    await refreshPeerBlocked(addDeviceForm.affiliateId, dt);
                  }}
                  style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                >
                  <option value="box">Box (eBox device)</option>
                  <option value="self-srt-connect">Self-SRT-Connect (direct internet, IP whitelist only)</option>
                </select>
                {addDeviceForm.device_type === "self-srt-connect" && (
                  <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:4 }}>
                    No box, no MAC address. The affiliate's IP is whitelisted directly on the router.
                  </div>
                )}
              </div>

              {addDeviceForm.device_type === "box" && (
                <div>
                  <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>MAC Address *</div>
                  <input
                    value={addDeviceForm.mac}
                    onChange={(e) => setAddDeviceForm(p => ({ ...p, mac: e.target.value }))}
                    placeholder="AA:BB:CC:DD:EE:FF"
                    style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }}
                  />
                </div>
              )}

              {addDeviceForm.device_type === "box" && (
                <div>
                  <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>
                    Device Model
                  </div>
                  <select
                    value={addDeviceForm.model}
                    onChange={(e) => {
                      const newModel = e.target.value;
                      setAddDeviceForm(p => ({ ...p, model: newModel, channelId: '', channelIds: [] }));
                    }}
                    style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                  >
                    <option value="">— Select model —</option>
                    {allModelsForAdd.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              )}

              {addDeviceForm.device_type === "box" && (() => {
                // Output type decides the channel cap together with the model:
                // HDMI is always 1 channel, IP out is 6 on an RK3568.
                const outMax = maxChannelsFor(addDeviceForm.model, addDeviceForm.output_type);
                return (
                  <div>
                    <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>
                      Output Type
                    </div>
                    <select
                      value={addDeviceForm.output_type || "ip"}
                      onChange={(e) => setAddDeviceForm(p => ({
                        ...p, output_type: e.target.value, channelId: '', channelIds: [],
                      }))}
                      style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                    >
                      {OUTPUT_TYPE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:4 }}>
                      {OUTPUT_TYPE_OPTIONS.find(o => o.value === (addDeviceForm.output_type || "ip"))?.hint}
                      {addDeviceForm.model && ` This device can hold ${outMax} channel${outMax === 1 ? "" : "s"}.`}
                    </div>
                  </div>
                );
              })()}

              <div>
                <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>
                  IP Address {addDeviceForm.device_type === "self-srt-connect" ? "*" : "(optional)"}
                </div>
                <input
                  value={addDeviceForm.ip_address}
                  onChange={(e) => setAddDeviceForm(p => ({ ...p, ip_address: e.target.value }))}
                  placeholder="192.168.1.50"
                  style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"'IBM Plex Mono', monospace", outline:"none" }}
                />
              </div>

              {(addDeviceForm.device_type === "self-srt-connect" || addDeviceForm.affiliateId) && (() => {
                const channelRequired = addDeviceForm.device_type === "self-srt-connect";
                const maxCh = maxChannelsFor(addDeviceForm.model, addDeviceForm.output_type);
                const isMulti = maxCh > 1;

                if (isMulti) {
                  const picked = addDeviceForm.channelIds || [];
                  const available = allChannelsForAdd.filter(c => !picked.includes(c.id) && !peerBlockedChannels.includes(c.id));
                  const slotsLeft = maxCh - picked.length;
                  return (
                    <div>
                      <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>
                        Assign Channels (up to {maxCh}) — {picked.length}/{maxCh} selected
                      </div>

                      {picked.length > 0 && (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                          {picked.map(chId => {
                            const ch = allChannelsForAdd.find(c => c.id === chId);
                            return (
                              <span key={chId} style={{ display:"inline-flex", alignItems:"center", gap:5, background:"var(--surface-alt)", border:`1px solid var(--border)`, borderRadius:5, padding:"3px 8px", fontSize:11, fontFamily:"'IBM Plex Mono', monospace", color:"var(--ink)" }}>
                                {chId}{ch ? ` · ${ch.display_name}` : ""}
                                <button
                                  onClick={() => setAddDeviceForm(p => ({ ...p, channelIds: p.channelIds.filter(id => id !== chId) }))}
                                  style={{ background:"none", border:"none", color:"var(--text-dim)", cursor:"pointer", fontSize:13, lineHeight:1, padding:0, fontFamily:"inherit" }}
                                >×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {slotsLeft > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            if (!e.target.value) return;
                            setAddDeviceForm(p => ({ ...p, channelIds: [...(p.channelIds || []), e.target.value] }));
                          }}
                          style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                        >
                          <option value="">— Add channel ({slotsLeft} slot{slotsLeft !== 1 ? "s" : ""} remaining) —</option>
                          {available.map(c => (
                            <option key={c.id} value={c.id}>{c.id} · {c.display_name}{takenChannelsForAdd.includes(c.id) ? " (in use elsewhere)" : ""}</option>
                          ))}
                        </select>
                      )}
                      <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:4 }}>
                        {addDeviceForm.model} supports up to {maxCh} channels. Channels already assigned to another box device on this affiliate are hidden — a channel may appear once per device type (box / self-SRT).
                      </div>
                    </div>
                  );
                }

                return (
                  <div>
                    <div style={{ fontSize:11, color:"var(--text-dim)", fontWeight:600, marginBottom:5 }}>
                      Assign Channel {channelRequired ? "*" : "(optional)"}
                    </div>
                    <select
                      value={addDeviceForm.channelId}
                      onChange={(e) => setAddDeviceForm(p => ({ ...p, channelId: e.target.value }))}
                      style={{ width:"100%", boxSizing:"border-box", background:"var(--surface)", border:`1px solid var(--border)`, borderRadius:6, padding:"8px 10px", fontSize:12, color:"var(--text)", fontFamily:"inherit", outline:"none", cursor:"pointer" }}
                    >
                      <option value="">{channelRequired ? "— Select channel —" : "— No channel —"}</option>
                      {allChannelsForAdd.filter(c => !peerBlockedChannels.includes(c.id)).map(c => (
                        <option key={c.id} value={c.id}>
                          {c.id} · {c.display_name}{takenChannelsForAdd.includes(c.id) ? " (in use elsewhere)" : ""}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize:10, color:"var(--text-dim)", marginTop:4 }}>
                      {channelRequired
                        ? "Required for Self-SRT-Connect devices. A channel may appear once per device type (box / self-SRT) on this affiliate."
                        : "A channel may appear once per device type (box / self-SRT) — same-type channels on this affiliate are hidden."}
                    </div>
                  </div>
                );
              })()}
            </div>

            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:22 }}>
              <Btn onClick={() => setShowAddDevice(false)}>Cancel</Btn>
              <Btn primary onClick={handleAddDevice} disabled={addDeviceSaving}>
                {addDeviceSaving ? "Adding…" : "Add Device"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={worksheetInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={(e) => handleWorksheetUpload(e.target.files?.[0])}
      />

      {/* ── Worksheet Modal ── */}
      {showWorksheetModal && (
        <div
          onClick={() => { setShowWorksheetModal(false); setWorksheetResult(null); }}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding:"28px", width:480, maxWidth:"90vw", boxShadow:"0 8px 40px rgba(0,0,0,0.15)", fontFamily:"inherit" }}
          >
            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:700, color:"var(--ink)" }}>Upload Device Worksheet</div>
                <div style={{ fontSize:12, color:"var(--text-sub)", marginTop:3 }}>Bulk-add devices from a CSV file</div>
              </div>
              <button
                onClick={() => { setShowWorksheetModal(false); setWorksheetResult(null); }}
                style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-dim)", fontSize:20, padding:"2px 6px" }}
              >×</button>
            </div>

            {/* Step 1 */}
            <div style={{ background:"var(--surface-alt)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:600, color:"var(--ink)", marginBottom:6 }}>Step 1 — Download the template</div>
              <div style={{ fontSize:11, color:"var(--text-dim)", marginBottom:8, fontFamily:"monospace", background:"var(--surface)", border:"1px solid var(--border)", borderRadius:5, padding:"6px 10px", lineHeight:1.7 }}>
                device_id, mac_address, ip_address,<br/>device_model, app_version, affiliate_name, affiliate_id
              </div>
              <div style={{ fontSize:11, color:"var(--text-dim)", marginBottom:10 }}>
                <strong style={{ color:"var(--text-sub)" }}>Required:</strong> device_id, mac_address, affiliate_id&nbsp;&nbsp;
                <strong style={{ color:"var(--text-sub)" }}>Optional:</strong> ip_address, device_model, app_version
              </div>
              <button
                onClick={downloadTemplate}
                style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:7, padding:"7px 14px", fontSize:12, fontWeight:600, color:"var(--ink)", cursor:"pointer", fontFamily:"inherit" }}
              >↓ Download Template CSV</button>
            </div>

            {/* Step 2 */}
            <div style={{ background:"var(--surface-alt)", border:"1px solid var(--border)", borderRadius:8, padding:"14px 16px", marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:600, color:"var(--ink)", marginBottom:6 }}>Step 2 — Upload filled worksheet</div>
              <div style={{ fontSize:12, color:"var(--text-sub)", marginBottom:12, lineHeight:1.5 }}>
                Upload your completed CSV. Devices are added row by row. Rows with errors are skipped and reported below.
              </div>
              <button
                onClick={() => worksheetInputRef.current?.click()}
                disabled={worksheetUploading}
                style={{ background:worksheetUploading?"var(--surface-alt)":"var(--ink)", border:"none", borderRadius:7, padding:"8px 18px", fontSize:12, fontWeight:600, color:worksheetUploading?"var(--text-dim)":"#fff", cursor:worksheetUploading?"not-allowed":"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:7 }}
              >
                {worksheetUploading
                  ? <><span style={{ display:"inline-block", width:12, height:12, border:"2px solid var(--border)", borderTopColor:"var(--text-sub)", borderRadius:"50%", animation:"ws-spin 0.7s linear infinite" }} /> Processing…</>
                  : "↑ Upload CSV"}
              </button>
            </div>

            {/* Results */}
            {worksheetResult && (
              <div style={{ borderRadius:8, overflow:"hidden", border:"1px solid var(--border)" }}>
                {worksheetResult.added.length > 0 && (
                  <div style={{ background:"var(--green-bg,#f0fdf4)", borderBottom:worksheetResult.errors.length?"1px solid var(--border)":"none", padding:"10px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--green,#16a34a)", marginBottom:4 }}>
                      ✓ {worksheetResult.added.length} device{worksheetResult.added.length !== 1 ? "s" : ""} added successfully
                    </div>
                    <div style={{ fontSize:11, color:"var(--green,#16a34a)", fontFamily:"monospace" }}>{worksheetResult.added.join(", ")}</div>
                  </div>
                )}
                {worksheetResult.errors.length > 0 && (
                  <div style={{ background:"var(--red-bg,#fef2f2)", padding:"10px 14px" }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--red,#dc2626)", marginBottom:6 }}>
                      ✕ {worksheetResult.errors.length} row{worksheetResult.errors.length !== 1 ? "s" : ""} failed
                    </div>
                    {worksheetResult.errors.map((err, i) => (
                      <div key={i} style={{ fontSize:11, color:"var(--red,#dc2626)", marginBottom:3 }}>
                        <strong>{err.id}</strong> — {err.message}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <style>{`@keyframes ws-spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      )}
    </>
  );
}